import { PaymentAvailabilityService } from "src/app/services/payment-availability.service";
import { isTripReturnWithoutOverpayment, PaymentContext } from "src/app/models/payment-availability.model";
import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { NgxSpinnerService } from "ngx-spinner";

import { BehaviorSubject, lastValueFrom, Subject, Subscription } from "rxjs";

import { MatDialog } from "@angular/material/dialog";

import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SaleTripService } from "./sale-trip.service";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { FormInputComponent } from "../../../components/ui-components/forms/form-input/form-input.component";
import { DropdownComponent } from "../../../components/ui-components/dropdown/dropdown.component";
import { DropdownOption } from "src/app/components/ui-components/dropdown/dropdown.model";
import { CardButtonComponent } from "../../../components/ui-components/buttons/card-button/card-button.component";
import { FormContainerComponent } from "../../../components/ui-components/forms/form-container/form-container.component";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { CashPaymentDialogComponent } from "../../../components/cash-payment-dialog/cash-payment-dialog.component";
import { SharedModule } from "../../../shared.module";
import { StateService } from "../../../services/state.service";
import { getVoucherCode, phoneNumberValidator } from "../../../functions/common-functions";
import {
  ActionButtonSize,
  ActionButtonType,
} from "../../../components/ui-components/buttons/action-button/action-button.model";
import {
  CardButtonBackground,
  CardButtonSize,
} from "../../../components/ui-components/buttons/card-button/card-button.model";
import { FormInputType } from "../../../components/ui-components/forms/form-input/form-input.model";
import {
  IBenefitsItem,
  IPaymentAttribute,
  LoginService,
  SharedService,
} from "@dert-services";
import { QueryParamEnum } from "src/app/enums/query-param.enum";
import { SobitService } from "src/app/services/sobit.service";
import { PaymentTypesService } from "src/app/services/payment-types.service";
import { PaymentsComponent } from "src/app/components/payments/payments.component";
import { IAddress, ICustomer, IPayment, IReservationData } from "src/app/services/model/get-trip-data.model";
import { PaymentsCalendarComponent } from "src/app/components/payments-calendar/payments-calendar.component";
import { PriceContainerComponent } from "src/app/components/price-container/price-container.component";
import { BenefitPaymentDialogComponent } from "src/app/components/benefit-payment-dialog/benefit-payment-dialog.component";
import { OfflinePaymentDialogContext, PaymentDialogModel, PaymentDialogResult, ToPaymentDialogModel } from "src/app/models/payment-dialog.model";
import { CardPaymentDialogComponent } from "src/app/components/card-payment-dialog/card-payment-dialog.component";
import { OfflineCardPaymentDialogComponent } from "src/app/components/offline-card-payment-dialog/offline-card-payment-dialog.component";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { VoucherPaymentDialogComponent } from "src/app/components/voucher-payment-dialog/voucher-payment-dialog.component";
import { StateDropdownService } from "src/app/services/state-dropdown.service";
import { OthersPaymentDialogComponent } from "src/app/components/others-payment-dialog/others-payment-dialog.component";
import { PopUpDialogInformalService } from "src/app/services/confirms/popUpDialogInformal.service";
import { PopUpInformalType } from "src/app/components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import {
  ReturnPaymentMethod,
  isAuthorizedForReturnPaymentMethod,
} from "src/app/guards/authorization-policy";

@Component({
  providers: [PaymentAvailabilityService],
  selector: "app-sale-trip",
  templateUrl: "./sale-trip.component.html",
  styleUrls: ["./sale-trip.component.scss"],
  imports: [
    TranslateModule,
    CardButtonComponent,
    FormInputComponent,
    DropdownComponent,
    CommonModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    SharedModule,
    PaymentsComponent,
    PaymentsCalendarComponent,
    PriceContainerComponent
],
  standalone: true,
})
export class SaleTripComponent implements OnInit, OnDestroy {
  readonly paymentAvailability = inject(PaymentAvailabilityService);
  private loggedCrossSaleContextKey = "";

  get paymentContext(): PaymentContext {
    const marketCode = this.tripData?.reservation?.systemInfo?.reservationProperties
      ?.find(property => property.key === "MarketCode")
      ?.value?.trim();
    const crossSaleMarketCodes = this.stateService.configuration?.crossSaleMarketCodes ?? [];
    const isCrossSale = !!marketCode && crossSaleMarketCodes.some(
      code => typeof code === "string"
        && code.trim().toLocaleLowerCase() === marketCode.toLocaleLowerCase(),
    );
    const countryCode = localStorage.getItem("countryCode");
    const businessUnit = this._loginService.user?.SelectedBusinessUnit ?? "unknown";
    const crossSaleContextKey = `${this.tripData?.reservation?.reservationId ?? ""}:${businessUnit}:${countryCode?.toLocaleLowerCase() ?? "(missing)"}:${marketCode?.toLocaleLowerCase() ?? ""}`;

    if (isCrossSale && crossSaleContextKey !== this.loggedCrossSaleContextKey) {
      console.info(
        `[SaleTripComponent] Cross-sale detected for configured countryCode '${countryCode ?? "(missing)"}' (BusinessUnit '${businessUnit}') and MarketCode '${marketCode}'. Benefit payment methods are hidden.`,
      );
      this.loggedCrossSaleContextKey = crossSaleContextKey;
    }

    return {
      operation: this.isReturn ? "return" : "sale",
      documentType: "trip",
      isCrossSale,
      // DEV-14262: the dialog must apply the same zero-balance/underpayment rule as the Others button.
      remainingToPay: this.tripData?.remainingToPay,
    };
  }

  private preparePaymentDialog(data: PaymentDialogModel): Promise<PaymentDialogModel> {
    return this.paymentAvailability.prepare(data, this.paymentContext);
  }


  readonly stateService = inject(StateService);
  private _saleTripService = inject(SaleTripService);
  private _spinner = inject(NgxSpinnerService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _sharedService = inject(SharedService);
  private _dialog = inject(MatDialog);
  private _sobitService = inject(SobitService);
  private _paymentTypesService = inject(PaymentTypesService);
  private _loginService = inject(LoginService);
  private _translate = inject(TranslateService);
  private _stateDropdownService = inject(StateDropdownService);
  private _popUpDialogInformalService = inject(PopUpDialogInformalService);

  isCardPaymentEnabled = this._sobitService.allowPaymentByCard;

  @ViewChild("scrollContainer")
  private scrollContainer!: ElementRef;

  loading: BehaviorSubject<boolean> = new BehaviorSubject(true);

  ActionButtonSize = ActionButtonSize;
  ActionButtonType = ActionButtonType;
  CardButtonSize = CardButtonSize;
  CardButtonBackground = CardButtonBackground;
  FormInputType = FormInputType;
  readonly ReturnPaymentMethod = ReturnPaymentMethod;
  isInPayment: boolean = false;
  tripData: IReservationData = null;
  tripDataAsync = signal<IReservationData>(null);
  showBenefitPayment = false;

  tripPayerForm: FormGroup;

  leftToPay: number = 0;
  voucherValueLeft: number | null = null;

  selectedBenefitType: TenderType | null = null;
  isReturn: boolean = false;
  benefitPaymentEnabled = this._paymentTypesService.benefitPaymentEnabled;

  private destroyObservable$ = new Subject<any>();
  private voucherQuerySubscription?: Subscription;

  public voucherCodeFromQuery?: string;
  public showVoucherButton = false;

  public checkSobitPaymentEnabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public payments: BehaviorSubject<IPayment[]> = new BehaviorSubject<IPayment[]>([]);
  public stateOptions: DropdownOption[] = [];

  constructor() {
    this.payments = this._saleTripService.payments;
  }

  async ngOnInit() {
    await this.paymentAvailability.refresh();

    this.isReturn = this._route.snapshot.data["type"] === "return";
    this.isInPayment = this.isReturn;
    this.voucherQuerySubscription = this._route.queryParams.subscribe((params) => {
      const voucherParam = params[QueryParamEnum.Voucher];
      this.voucherCodeFromQuery = typeof voucherParam === "string" ? voucherParam : undefined;
      this.showVoucherButton = !!this.voucherCodeFromQuery;
      this.isInPayment = this.isReturn || !!this.voucherCodeFromQuery;
      this.updateTripPayerFormState();
    });
    await this.updateStateOptions();
    await this.getTripData();
    await this._paymentTypesService.getBenefitTypesWithNominals(this.paymentContext); /* enables benefity payment button */
    await this.loading.next(false);
  }

  private async updateStateOptions(ensureValue?: string): Promise<void> {
    this.stateOptions = await this._stateDropdownService.getStateOptions(ensureValue);
  }

  async ngAfterViewChecked() {

        

  }

  private initTripData() {
    if (!this.tripData) {
      return;
    }

    this.tripData.reservation.customer ??= { } as ICustomer;
    this.tripData.reservation.customer.address ??= {
      street: '',
      countryISO3Code: '',
      city: '',
      streetNo: '',
     } as IAddress;

    if (!this.isReturn) {
      this.leftToPay = this.tripData.reservation.paymentSummary.totalPrice.amount - this.tripData.reservation.paymentSummary.totalPaid.amount || 0;
    } else if (this.isReturn) {
      this.leftToPay =
        this.tripData.reservation.paymentSummary.totalPrice.amount <= this.tripData.reservation.paymentSummary.totalPaid.amount
          ? this.tripData.reservation.paymentSummary.totalPrice.amount
          : this.tripData.reservation.paymentSummary.totalPaid.amount;
    }
  }

  private async createTripPayerForm() {
    const payerData = this.tripData.reservation.customer || ({} as ICustomer);

    payerData.address ??= {} as IAddress;

    this.tripPayerForm = new FormGroup<{
      ordererName: FormControl<string>;
      ordererSurname: FormControl<string>;
      street: FormControl<string>;
      city: FormControl<string>;
      state: FormControl<string>;
      postalCode: FormControl<string>;
      emailAddress: FormControl<string>;
      CIN: FormControl<string | null>;
      TIN: FormControl<string | null>;
    }>({
      ordererName: new FormControl(payerData?.firstName, [Validators.required]),
      ordererSurname: new FormControl(payerData?.lastName, [ Validators.required, ]),
      street: new FormControl(payerData?.address.street + (' ' + payerData?.address.streetNo).trimRight(), [Validators.required]),
      city: new FormControl(payerData?.address.city, [Validators.required]),
      state: new FormControl(payerData?.address.countryISO3Code, [Validators.required]),
      postalCode: new FormControl(payerData?.address.zipCode, [Validators.required]),
      emailAddress: new FormControl(payerData?.emailAddress, [Validators.required, Validators.email]),
      CIN: new FormControl(payerData?.CIN),
      TIN: new FormControl(payerData?.TIN),
    });

    await this.updateStateOptions(payerData.address?.countryISO3Code);
  }

  async payByCard() {
    if (!this.canUseReturnPaymentMethod(ReturnPaymentMethod.Card)) {
      return;
    }

    this.checkSobitPaymentEnabled.next(false);
    const useOfflineCardPayment = !this.isCardPaymentEnabled();

    const payments = await this._paymentTypesService.getPaymentTypesWithNominals(this.paymentContext);

    const currencyISOCode = this._loginService.user.CurrencyCode;;
    const selectedPayment = payments.find(p => p.TenderType === TenderType.Card && p.Currency === currencyISOCode);

    let result = await this.openCardPaymentDialog(useOfflineCardPayment);
    this.checkSobitPaymentEnabled.next(true);
    if (result) {
      const emailAddress = this.tripPayerForm.get('emailAddress')?.value || '';
      console.log('[SaleTripComponent.payByCard] Form emailAddress value:', emailAddress);
      console.log('[SaleTripComponent.payByCard] tripPayerForm values:', this.tripPayerForm.value);
      const paymentResult = await this._saleTripService.processPayment(
        TenderType.Card,
        result.PaymentName || selectedPayment.Name,
        this.tripPayerForm.value,
        result.Amount,
        this.isReturn,
        result.PaymentAttributes,
        emailAddress,
      );

      if (!paymentResult.success && !this.isReturn && paymentResult.paymentUID && paymentResult.headerUid) {
        if (paymentResult.paymentOutcome === "handled") {
          return;
        }

        if (useOfflineCardPayment) {
          await this._saleTripService.registerOfflineCardPayment(
            paymentResult.paymentUID,
            paymentResult.headerUid,
            result.PaymentAttributes,
            emailAddress,
          );
          await this.getTripData();
          return;
        }

        const continueOffline = await this.confirmOfflinePaymentFallback(paymentResult.errorMessage);

        if (continueOffline) {
          const offlineResult = await this.openCardPaymentDialog(true, result.Amount);

          if (offlineResult) {
            await this._saleTripService.registerOfflineCardPayment(
              paymentResult.paymentUID,
              paymentResult.headerUid,
              offlineResult.PaymentAttributes,
              emailAddress,
            );
          }
        }
      }

      await this.getTripData();
    }
    
  }

  private async openCardPaymentDialog(
    offlineMode = false,
    initialAmountToPay?: number,
    offlinePaymentContext?: OfflinePaymentDialogContext | null,
  ): Promise<IPayment | null> {
    const baseData: PaymentDialogModel = {
      ...(await this.preparePaymentDialog(ToPaymentDialogModel(this.tripData, this.isReturn))),
      initialAmountToPay: initialAmountToPay ?? null,
      offlinePaymentContext: offlinePaymentContext ?? null,
    };

    if (offlineMode) {
      const dialogRef = this._dialog.open<OfflineCardPaymentDialogComponent, PaymentDialogModel, IPayment>(
        OfflineCardPaymentDialogComponent,
        { panelClass: "full-screen-dialog", data: baseData },
      );
      return lastValueFrom(dialogRef.afterClosed());
    }

    const dialogRef = this._dialog.open<CardPaymentDialogComponent, PaymentDialogModel, PaymentDialogResult>(
      CardPaymentDialogComponent,
      { panelClass: "full-screen-dialog", data: baseData },
    );
    const result = await lastValueFrom(dialogRef.afterClosed());

    // Open the offline form from the parent to avoid stacking two full-screen
    // Material dialogs. The amount and payment metadata come from the closed form.
    if (!result?.openOfflineCardPaymentDialog) {
      return result;
    }

    return this.openCardPaymentDialog(true, result.Amount, result.offlinePaymentDialogContext);
  }

  private async confirmOfflinePaymentFallback(errorMessage?: string | null): Promise<boolean> {
    const result = await this._popUpDialogInformalService.confirm({
      title: "webPos.Errors.Error",
      message: "webPos.PaymentWithCardFailed",
      subMessage: errorMessage || undefined,
      type: PopUpInformalType.Error,
      confirmButtonText: "webPos.PayOffline",
      cancelButtonText: "webPos.Cancel",
      showCancelButton: true,
    });

    return !!result?.confirmed;
  }

  async payByCash() {
    if (!this.canUseReturnPaymentMethod(ReturnPaymentMethod.Cash)) {
      return;
    }

    const payments = await this._paymentTypesService.getPaymentTypesWithNominals(this.paymentContext);
    const currencyISOCode = this._loginService.user.CurrencyCode;;
    const selectedPayment = payments.find(p => p.TenderType === TenderType.Cash && p.Currency === currencyISOCode);

    const dialogRef = this._dialog.open<CashPaymentDialogComponent, PaymentDialogModel, IPayment>(CashPaymentDialogComponent, {
      panelClass: "full-screen-dialog",
      data: (await this.preparePaymentDialog(ToPaymentDialogModel(this.tripData, this.isReturn))),
    });

    const result = await lastValueFrom(dialogRef.afterClosed());
    if (result) {
      const emailAddress = this.tripPayerForm.get('emailAddress')?.value || '';
      await this._saleTripService.processPayment(
        TenderType.Cash,
        selectedPayment.Name,
        this.tripPayerForm.value,
        result.Amount,
        this.isReturn,
        undefined,
        emailAddress,
      );
      await this.getTripData();
    }
  }

  payByVoucher() {
    if (!this.canUseReturnPaymentMethod(ReturnPaymentMethod.Voucher)) {
      return;
    }

    const reservationId = this.tripData?.reservation?.reservationId;
    if (!reservationId) {
      return;
    }
    this._router.navigate(["/voucher-return", reservationId], {
      queryParams: { source: "trip" },
    });
  }

  async payByOthers() {
    // DEV-14262: direct calls must respect the same loaded data, form, roles, and availability as the button.
    if (!this.tripData || !this.tripPayerForm?.valid
      || !this.canUseReturnPaymentMethod(ReturnPaymentMethod.Other)
      || this.areReturnPaymentActionsDisabled(ReturnPaymentMethod.Other)) {
      return;
    }

    const dialogRef = this._dialog.open<OthersPaymentDialogComponent, PaymentDialogModel, PaymentDialogResult>(OthersPaymentDialogComponent, {
      panelClass: "full-screen-dialog",
      data: (await this.preparePaymentDialog(ToPaymentDialogModel(this.tripData, this.isReturn))),
    });
    let result = await lastValueFrom(dialogRef.afterClosed());

    // Others can contain terminal-backed payment types (for example MALLCARD).
    // Resolve the marker before processing so Sobit is skipped only after offline confirmation.
    if (result?.openOfflineCardPaymentDialog && result.offlinePaymentDialogContext) {
      result = await this.openCardPaymentDialog(
        true,
        result.Amount,
        result.offlinePaymentDialogContext,
      );
    }

    if (result) {
      const emailAddress = this.tripPayerForm.get('emailAddress')?.value || '';
      await this._saleTripService.processPayment(
        result.PaymentType,
        result.PaymentName,
        this.tripPayerForm.value,
        result.Amount,
        this.isReturn,
        result.PaymentAttributes,
        emailAddress,
      );
      await this.getTripData();
    }
  }

  async payByBenefits(paymentData: PaymentDialogResult) {
    const emailAddress = this.tripPayerForm.get('emailAddress')?.value || '';
    await this._saleTripService.processPayment(
      paymentData.PaymentType,
      paymentData.PaymentName,
      this.tripPayerForm.value,
      paymentData.Amount,
      this.isReturn,
      paymentData.PaymentAttributes,
      emailAddress,
    );

    await this.getTripData();
  }

  private async handleBenefitTerminalPayment(paymentData: PaymentDialogResult): Promise<void> {
    const emailAddress = this.tripPayerForm.get('emailAddress')?.value || '';
    const offlinePaymentContext = paymentData.offlinePaymentDialogContext ?? {
      paymentType: paymentData.PaymentType,
      paymentName: paymentData.PaymentName || "",
      paymentDisplayName: paymentData.PaymentDescription || paymentData.PaymentName || "",
      currency: this._loginService.user.CurrencyCode,
      paymentAttributes: paymentData.PaymentAttributes || [],
    };

    if (!this._sobitService.allowPaymentByCard()) {
      // Scenario 2: Sobit service unavailable — no sub-error
      const continueOffline = await this.confirmOfflinePaymentFallback(null);
      if (continueOffline) {
        const offlineResult = await this.openCardPaymentDialog(true, paymentData.Amount, offlinePaymentContext);
        if (offlineResult) {
          await this.payByBenefits(offlineResult);
          return;
        }
      }
      await this.getTripData();
      return;
    }

    const paymentResult = await this._saleTripService.processPayment(
      paymentData.PaymentType,
      paymentData.PaymentName,
      this.tripPayerForm.value,
      paymentData.Amount,
      this.isReturn,
      paymentData.PaymentAttributes,
      emailAddress,
    );

    if (!paymentResult.success && !this.isReturn && paymentResult.paymentUID && paymentResult.headerUid) {
      if (paymentResult.paymentOutcome === "handled") {
        return;
      }

      // Scenario 3: Sobit available but payment failed — show sub-error
      const continueOffline = await this.confirmOfflinePaymentFallback(paymentResult.errorMessage);
      if (continueOffline) {
        const offlineResult = await this.openCardPaymentDialog(true, paymentData.Amount, offlinePaymentContext);
        if (offlineResult) {
          await this._saleTripService.registerOfflineCardPayment(
            paymentResult.paymentUID,
            paymentResult.headerUid,
            offlineResult.PaymentAttributes,
            emailAddress,
          );
        }
      }
    }

    await this.getTripData();
  }

  async showBenefitPaymentForm() {
    if (!this.canUseReturnPaymentMethod(ReturnPaymentMethod.Benefits)) {
      return;
    }

    const paymentModel = (await this.preparePaymentDialog(ToPaymentDialogModel(this.tripData as IReservationData)));

    const dialogRef = this._dialog.open<BenefitPaymentDialogComponent, PaymentDialogModel, PaymentDialogResult>(BenefitPaymentDialogComponent, {
      panelClass: "full-screen-dialog",
      disableClose: true,
      data: paymentModel,
    });

    const result = await lastValueFrom(dialogRef.afterClosed());
    if (!result) {
      return;
    }

    // A manager can explicitly choose manual registration for a terminal benefit.
    // In that case skip Sobit and continue with the metadata-preserving offline form.
    if (result.openOfflineCardPaymentDialog && result.offlinePaymentDialogContext) {
      const offlineResult = await this.openCardPaymentDialog(
        true,
        result.Amount,
        result.offlinePaymentDialogContext,
      );

      if (offlineResult) {
        await this.payByBenefits(offlineResult);
      }
      return;
    }

    if (result.PaymentType === TenderType.ElectronicVoucher) {
      await this.handleBenefitTerminalPayment(result);
    } else {
      await this.payByBenefits(result);
    }
  }

  goToPayment() {
    this.isInPayment = true;
    this.updateTripPayerFormState();
  }

  public canUseReturnPaymentMethod(method: ReturnPaymentMethod): boolean {
    const authorized = !this.isReturn || isAuthorizedForReturnPaymentMethod(this._loginService.user?.Roles, method);
    // Issuing a refund voucher is a separate operation, not a tender refund.
    return authorized && ((this.isReturn && method === ReturnPaymentMethod.Voucher)
      || this.paymentAvailability.has(this.paymentContext, method));
  }

  goToInfo() {
    this.isInPayment = false;
    this.updateTripPayerFormState();
  }

  public areReturnPaymentActionsDisabled(method?: ReturnPaymentMethod): boolean {
    if (!this.isReturn) {
      return false;
    }

    // DEV-14262: only Others can bypass the overpayment requirement, with configured and authorized SKODA.
    if (method === ReturnPaymentMethod.Other && isTripReturnWithoutOverpayment(this.paymentContext)) {
      return !this.canUseReturnPaymentMethod(method);
    }

    // Missing or invalid balances must keep regular refund actions disabled.
    return !Number.isFinite(this.tripData?.remainingToPay) || this.tripData.remainingToPay >= 0;
  }

  private updateTripPayerFormState(): void {
    if (!this.tripPayerForm) {
      return;
    }

    if (this.showVoucherButton) {
      this.tripPayerForm.enable();
      return;
    }

    if (this.isInPayment) {
      this.tripPayerForm.enable();
      return;
    }

    this.tripPayerForm.disable();
  }

  private scrollToBottom(): void {
    const container = this.scrollContainer.nativeElement;
    container.scrollTop = container.scrollHeight;
  }

  public async reloadData(): Promise<void> {
    await this.getTripData();
  }

  private async getTripData() {
    this._spinner.show();
    // Always reload the trip data from the service
    const bnr = this.tripData?.reservation.reservationId || this._sharedService.getQueryParamFromUrl(QueryParamEnum.BNR);
    
    if (bnr) {
      await this._saleTripService.getTrip(bnr);
    }

    await this._sobitService.generateClient();

    this.tripData = this._saleTripService.tripData;
    this.tripDataAsync.set(this.tripData);

    if (!this.tripData) {
      this._spinner.hide();
      return;
    }

    this.initTripData();
    await this.createTripPayerForm();
    this.updateTripPayerFormState();

    this._spinner.hide();
  }

  ngOnDestroy() {
    this.voucherQuerySubscription?.unsubscribe();
    this.destroyObservable$.next(true);
    this.destroyObservable$.complete();
  }

  public async applyVoucher(): Promise<void> {
    if (!this.voucherCodeFromQuery || !this.paymentAvailability.has(this.paymentContext, "voucher") || !this.tripData || !this.tripPayerForm) {
      return;
    }

    // Check if voucher payment with the same voucher code already exists
    const existingVoucherPayment = this.payments.value?.find(
      p => p.PaymentType === TenderType.Voucher && getVoucherCode(p) === this.voucherCodeFromQuery
    );

    if (existingVoucherPayment) {
      await this._sharedService.throwErrorDialog(
        this._translate.instant('webPos.Voucher.VoucherPaymentAlreadyExists')
      );
      return;
    }

    const reservationId = this.tripData.reservation.reservationId;
    const voucherSource =
      this.tripData.reservation.systemInfo?.sourceSystem ?? "PosNG";
    const dialogRef = this._dialog.open<
      VoucherPaymentDialogComponent,
      PaymentDialogModel & {
        voucherCode?: string;
        voucherBnr?: number;
        voucherSource?: string;
      },
      IPayment
    >(VoucherPaymentDialogComponent, {
      data: {
        ...(await this.preparePaymentDialog(ToPaymentDialogModel(this.tripData, this.isReturn))),
        voucherCode: this.voucherCodeFromQuery,
        voucherBnr: Number(reservationId),
        voucherSource,
      },
    });

    const payment = await lastValueFrom(dialogRef.afterClosed());
    if (!payment) {
      return;
    }

    const emailAddress = this.tripPayerForm.get('emailAddress')?.value || '';

    const paymentResult = await this._saleTripService.processPayment(
      TenderType.Voucher,
      payment.PaymentName,
      this.tripPayerForm.value,
      payment.Amount,
      this.isReturn,
      payment.PaymentAttributes,
      emailAddress,
    );

    if (paymentResult?.success) {
      await this.resumeStandardTripPaymentFlowAfterVoucher();
    }
  }

  private async resumeStandardTripPaymentFlowAfterVoucher(): Promise<void> {
    const reservationId = this.tripData?.reservation?.reservationId;
    if (!reservationId) {
      return;
    }

    const navigationSucceeded = await this._router.navigate(["/trip"], {
      queryParams: { bnr: reservationId },
      replaceUrl: true,
    });

    if (!navigationSucceeded) {
      return;
    }

    this.voucherCodeFromQuery = undefined;
    this.showVoucherButton = false;

    await this.getTripData();

    if (this.leftToPay > 0) {
      this.goToPayment();
    } else {
      this.goToInfo();
    }
  }
}
